import { toLatinDigits, toPersianDigits } from './digits.js';

/**
 * Stock quantities (ADR-030): decimal strings with at most three decimals, like `"2.5"` or `"0.4"`.
 * The database stores `numeric(12,3)`; the API carries strings; arithmetic happens in whole
 * thousandths (`bigint`), so 0.1 + 0.2 is exactly 0.3. Never a JavaScript floating-point number.
 */

const QUANTITY = /^-?(0|[1-9][0-9]{0,8})(\.[0-9]{1,3})?$/;
const SCALE = 1000n;

/** A signed quantity string, up to 999,999,999.999 (what `numeric(12,3)` holds). */
export function isQuantityString(value: string): boolean {
  return QUANTITY.test(value);
}

/** `"2.5"` → `2500n` (thousandths). Throws on anything that is not a quantity string. */
export function parseQuantity(value: string): bigint {
  if (!QUANTITY.test(value)) throw new RangeError(`Invalid quantity: ${value}`);
  const negative = value.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? value.slice(1) : value).split('.');
  const milli = BigInt(whole) * SCALE + BigInt(fraction.padEnd(3, '0'));
  return negative ? -milli : milli;
}

/** `2500n` → `"2.5"`; `400n` → `"0.4"`; trailing zeros dropped. */
export function formatQuantity(milli: bigint): string {
  const negative = milli < 0n;
  const abs = negative ? -milli : milli;
  const whole = abs / SCALE;
  const fraction = (abs % SCALE).toString().padStart(3, '0').replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

/**
 * A quantity typed by a user: Persian or Arabic digits, `٫`, `.` or `,` as the decimal mark.
 * Returns thousandths, or null when it is not a number with at most three decimals.
 */
export function parseQuantityInput(text: string): bigint | null {
  const value = toLatinDigits(text).trim().replace(/[٫,]/g, '.');
  if (!/^[0-9]+(\.[0-9]{1,3})?$/.test(value) && !/^\.[0-9]{1,3}$/.test(value)) return null;
  const normalized = value.startsWith('.') ? `0${value}` : value.replace(/^0+(?=[0-9])/, '');
  return QUANTITY.test(normalized) ? parseQuantity(normalized) : null;
}

const VULGAR: Record<string, string> = { '½': '1/2', '⅓': '1/3', '⅔': '2/3', '¼': '1/4', '¾': '3/4', '⅕': '1/5', '⅖': '2/5', '⅗': '3/5', '⅘': '4/5', '⅙': '1/6', '⅛': '1/8' };
// `1/3`, `2 / 3`, or `1 1/2` (a whole number and a fraction), whole numbers up to nine digits.
const FRACTION = /^(?:([0-9]{1,9})\s+)?([0-9]{1,9})\s*\/\s*([0-9]{1,9})$/;

/** Half up division of non-negative bigints. */
const divRound = (n: bigint, d: bigint): bigint => (n * 2n + d) / (d * 2n);

/**
 * A quantity typed as a decimal (`2.5`, `۲٫۵`) or as a fraction (`1/3`, `1 1/2`, `½`), for what is
 * used from a vial or box where "a third" is how the doctor thinks. Returns thousandths (a fraction
 * is rounded half up to three decimals), or null when the text is neither. `exact` is false when
 * rounding changed the value, so the form can show the rounded number.
 */
export function parseQuantityFraction(text: string): { milli: bigint; exact: boolean } | null {
  let value = toLatinDigits(text).trim().replace(/[⁄∕／]/g, '/').replace(/\s*و\s*/g, ' ');
  // A vulgar fraction may follow a whole number (`1½`) or stand alone.
  value = value.replace(/([0-9])?([½⅓⅔¼¾⅕⅖⅗⅘⅙⅛])/g, (_m, whole: string | undefined, glyph: string) => `${whole ? `${whole} ` : ''}${VULGAR[glyph]}`);
  const m = FRACTION.exec(value);
  if (!m) {
    const plain = parseQuantityInput(value);
    return plain === null ? null : { milli: plain, exact: true };
  }
  const whole = BigInt(m[1] ?? '0');
  const num = BigInt(m[2] ?? '0');
  const den = BigInt(m[3] ?? '0');
  if (den === 0n) return null;
  const milli = whole * SCALE + divRound(num * SCALE, den);
  if (milli > 999_999_999_999n) return null;
  return { milli, exact: (num * SCALE) % den === 0n };
}

const FRACTION_DENOMINATORS = [2, 3, 4, 5, 6, 8, 10];

/**
 * For display, the inverse of `parseQuantityFraction`: a quantity within one thousandth of a simple
 * fraction (denominators 2, 3, 4, 5, 6, 8, 10; the smallest wins) reads as a fraction, `۱/۳`, or
 * `۱ و ۱/۲` with a whole part. Anything else falls back to the decimal form of `formatQuantityFa`.
 */
export function formatQuantityFraction(value: string | bigint): string {
  const milli = typeof value === 'bigint' ? value : parseQuantity(value);
  const negative = milli < 0n;
  const abs = negative ? -milli : milli;
  const whole = abs / SCALE;
  const rest = Number(abs % SCALE);
  if (rest !== 0) {
    for (const den of FRACTION_DENOMINATORS) {
      for (let num = 1; num < den; num++) {
        if (Math.abs(rest * den - num * 1000) <= den) {
          const frac = `${num}/${den}`;
          const text = whole === 0n ? frac : `${whole} و ${frac}`;
          return `${negative ? '-' : ''}${toPersianDigits(text)}`;
        }
      }
    }
  }
  return formatQuantityFa(milli);
}

/** For display: `"2.5"` → `۲٫۵`. */
export function formatQuantityFa(value: string | bigint): string {
  const text = typeof value === 'bigint' ? formatQuantity(value) : formatQuantity(parseQuantity(value));
  return toPersianDigits(text).replace('.', '٫');
}
